import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, PlusCircle, Eye, Pencil, Trash2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';
import useAuth from '../../../../hooks/useAuth';

export default function ReferralSourceList() {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null);

    useEffect(() => {
        api.get('/referral-sources')
            .then(r => setSources(r.data))
            .catch(() => toast.error('Failed to load referral sources'))
            .finally(() => setLoading(false));
    }, []);

    const handleDelete = async (id) => {
        if (!isAdmin) return;
        if (!window.confirm('Delete this referral source?')) return;
        setDeleting(id);
        try {
            await api.delete(`/referral-sources/${id}`);
            setSources(prev => prev.filter(s => s.id !== id));
            toast.success('Referral source deleted');
        } catch {
            toast.error('Failed to delete');
        } finally {
            setDeleting(null);
        }
    };

    return (
        <div>
            <Toaster position="top-right" richColors />
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Referral Sources</h2>
                        <p className="text-sm text-gray-500 mt-1">Manage facilities and contacts</p>
                    </div>
                    {isAdmin && (
                        <button onClick={() => navigate('/admin/referral-sources/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition">
                            <PlusCircle className="w-4 h-4" /> Add Source
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>
                ) : (
                    <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['Facility', 'Contact', 'Phone', 'Email', 'Actions'].map(h => (
                                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sources.length === 0 ? (
                                    <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400">No referral sources found</td></tr>
                                ) : sources.map(s => (
                                    <tr key={s.id} className="hover:bg-gray-50">
                                        <td className="px-5 py-3 font-medium text-gray-900">{s.facility_name}</td>
                                        <td className="px-5 py-3 text-sm text-gray-600">{s.contact_person || '—'}</td>
                                        <td className="px-5 py-3 text-sm text-gray-600">{s.phone || '—'}</td>
                                        <td className="px-5 py-3 text-sm text-gray-600">{s.email || '—'}</td>
                                        <td className="px-5 py-3">
                                            <div className="flex gap-2">
                                                <button onClick={() => navigate(`/admin/referral-sources/${s.id}`)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md"><Eye className="w-4 h-4" /></button>
                                                {isAdmin && (
                                                    <>
                                                        <button onClick={() => navigate(`/admin/referral-sources/${s.id}/edit`)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-md"><Pencil className="w-4 h-4" /></button>
                                                        <button onClick={() => handleDelete(s.id)} disabled={deleting === s.id} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50">
                                                            {deleting === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
